import { fourierConfig as config } from "./config";
import { dft, selectCoefficients } from "./math/dft";
import { sampleTrail, toSvgPath } from "./math/epicycles";
import { normalizeOutline, samplePath, type Outline } from "./math/samplePath";

/** Immutable geometry, built once per composition rather than per frame. */
export const createFourierModel = (
  outline: Outline,
  size = 1,
  circleCounts: readonly number[] = config.circleCounts,
) => {
  const outlineSamples = normalizeOutline(
    samplePath(outline, config.sampleCount),
  ).map((p) => ({ x: p.x * size, y: p.y * size }));
  const coefficients = dft(outlineSamples);
  const dc = coefficients.find((c) => c.frequency === 0)!;
  const stages = circleCounts.map((count, index) => {
    const selected = selectCoefficients(coefficients, count);
    const trail = sampleTrail(selected, dc, config.trailResolution);
    return {
      count,
      seconds: config.timing.stages[index],
      coefficients: selected,
      trail,
      fullPath: toSvgPath(trail),
    };
  });
  const final = stages[stages.length - 1].trail;
  const boundsCenter = {
    x:
      (Math.min(...final.map((p) => p.x)) +
        Math.max(...final.map((p) => p.x))) /
      2,
    y:
      (Math.min(...final.map((p) => p.y)) +
        Math.max(...final.map((p) => p.y))) /
      2,
  };
  return { outlineSamples, coefficients, dc, stages, boundsCenter };
};
export type FourierModel = ReturnType<typeof createFourierModel>;
export const heartModel = createFourierModel(config.outline);
export const { outlineSamples, coefficients, dc, stages } = heartModel;
